import { isNewType, isTimestamp } from './constants';
/**
 * BE contract -> what this screen renders.
 *
 * ONE FILE, deliberately. The eight components take their data as props and know
 * nothing about the wire shape, so a contract change lands here and nowhere else.
 *
 * Shapes are from EventFieldConfig.scala on
 * feat/field-configs-for-eventTimeline @ 11c814161d.
 */

/**
 * `EventTypeFieldEntry` needs NO adapting — the BE and this screen already agree
 * on all seven keys (fieldKey, displayLabel, showSpatial, showOnClick, sortOrder,
 * isIdentifier, isDeprecated). Kept as an explicit identity function rather than
 * left implicit, so the day it stops being true there is an obvious place to fix.
 *
 * The BE's own comment confirms two assumptions this screen was built on:
 *   "isIdentifier and isDeprecated are derived on read; ignored on write"
 * — which is why the table renders them but never sends them back.
 */
/**
 * @param payload   the configured-fields response for one event type
 * @param catalogue the category's available fields, used only to fill a
 *                  MISSING label (see below). Optional — omit it and the fields
 *                  come back exactly as sent.
 *
 * THE IDENTIFIER ARRIVES WITH NO `displayLabel`. Every other configured field
 * carries one; `activity_id` / `transaction_id` do not (measured against the
 * live BE, 2026-09-02):
 *   { fieldKey: 'activity_id', showSpatial: false, showOnClick: true,
 *     sortOrder: 0, isIdentifier: true, isDeprecated: false }
 * so its row rendered as a bare badge over a technical name, with the "Activity
 * ID" title simply absent.
 *
 * The catalogue DOES have it — `{ fieldKey: 'activity_id', displayLabel:
 * 'Activity ID', … }` — so the gap is filled from there rather than by
 * prettifying the key, which would read "Activity Id" and disagree with the
 * picker the admin chose it from. Falling back to the key itself is the last
 * resort, so a field the catalogue does not know still shows something.
 *
 * Only ABSENT labels are filled; a label the admin has set always wins.
 */
export const toFields = (payload, catalogue) => {
  const list = Array.isArray(payload) ? payload : payload?.fields || [];

  /* THE IDENTIFIER IS ALWAYS ON, IN BOTH COLUMNS — product requirement, and the
     table renders its checkboxes checked-and-DISABLED to say so. Forced here
     rather than only in the component so the DATA agrees with what is shown:
     `fields` and `baseline` are both built from this one call, so a freshly
     loaded type does not read as edited, and a save sends what the screen
     displays instead of the zeros the row may still hold.

     NOT CONDITIONAL ON THE STORED VALUE. It began as "default when 0/0", which
     left three of the seeded types checked and two unchecked depending on what
     happened to be in the table. A locked control cannot honour a stored
     preference, so there is nothing to preserve.

     ABOVE THE EARLY RETURN, deliberately: this must not depend on the
     catalogue. It first sat below, and whenever `availableFields` had not
     resolved — `loadFields` reads it from a ref, and the two fetches race — the
     function returned the list untouched and the rule silently did not apply.
     Nothing gave that away, because `displayLabel` now arrives from the BE too,
     so the label filling below had nothing visible to do either.

     NO SLOT IS SPENT on it — see the timestamp note below. */
  /* THE TIMESTAMP IS TREATED THE SAME WAY (FIN-13594). `txn_date_time` shipped
     ticked on the TRANSACTION default while `activity_datetime` did not on the
     ACTIVITY default, so activity events rendered with no time on the node or in
     the panel until someone ticked the boxes for every activity event type —
     including the `default` type every unknown activity_type falls back to.
     A timestamp is a primary attribute of any event; both fields sit at
     sort_order 1, straight after the identifier.

     BOTH CATEGORIES, and neither costs a slot: the caps count only the admin's
     own choices (FieldConfigTable `chosen`, the backend's `userFields`), and
     the timeline pins both fields outside the three node lines. */
  const withRequiredDefaults = list.map((field) =>
    field && (field.isIdentifier || isTimestamp(field.fieldKey))
      ? { ...field, showSpatial: true, showOnClick: true }
      : field
  );

  if (!Array.isArray(catalogue) || !catalogue.length) {
    return withRequiredDefaults;
  }

  // BOTH KEYS. The raw endpoint sends `displayLabel`; `toAvailableFields`
  // renames it to `displayName` for the picker, and that transformed list is
  // what the page holds in state and passes here. Reading only `displayLabel`
  // silently found nothing and fell through to the raw key, so the identifier
  // row read "activity_id" instead of "Activity ID" (2026-09-02).
  const labelFor = new Map(
    catalogue
      .filter((f) => f && f.fieldKey)
      .map((f) => [f.fieldKey, f.displayLabel || f.displayName])
  );

  return withRequiredDefaults.map((field) =>
    field && !field.displayLabel
      ? {
          ...field,
          displayLabel: labelFor.get(field.fieldKey) || field.fieldKey
        }
      : field
  );
};

/**
 * `AvailableField` DOES differ. The BE sends:
 *   { fieldKey, displayLabel, technicalName, isIdentifier }
 * and the picker was built against `{ technicalName, displayName }`.
 *
 * `fieldKey` and `technicalName` are NOT the same thing, and the difference is
 * the whole reason the earlier resolution gap existed. Per the BE's model
 * comment:
 *   fieldKey      = custom_field.name          — what the config stores
 *   technicalName = custom_field.technical_name — the StarRocks column behind it
 *
 * A field with a NULL technicalName has no StarRocks backing, so an admin can
 * configure it and the node will render nothing. Measured after running the
 * migration: 76 of 288 custom_field rows have one. `hasBacking` surfaces that so
 * the picker can warn rather than let it fail silently on the alert screen.
 */
export const toAvailableFields = (payload) => {
  const list = Array.isArray(payload) ? payload : payload?.fields || [];
  // DEDUPED BY fieldKey. Measured 2026-08-31: the endpoint returned 20 entries
  // for 10 fields — `custom_field` has two identical rows per field and no unique
  // key on (name, group_name), so any re-seed doubles it silently.
  //
  // The duplicates are not merely untidy: the picker keys selection on fieldKey,
  // so ticking one row ticked BOTH and the footer read "Add 1" against two
  // checked boxes. Deduping here fixes it for every consumer at once, and keeps
  // the screen correct on any environment whose data has the same problem.
  //
  // FIRST WINS: the rows are identical apart from their id, so there is nothing
  // to choose between them.
  const seen = new Set();
  return list
    .filter((f) => {
      if (!f || seen.has(f.fieldKey)) return false;
      seen.add(f.fieldKey);
      return true;
    })
    .map((f) => ({
      fieldKey: f.fieldKey,
      // The picker searches BOTH, so both names are kept.
      displayName: f.displayLabel,
      technicalName: f.technicalName,
      isIdentifier: Boolean(f.isIdentifier),
      hasBacking: f.technicalName != null && f.technicalName !== ''
    }));
};

/**
 * `EventTypeConfigResponse` carries more than the list needs — username,
 * eventCategory and createdAt are passed through untouched for the callers that
 * want them (the "New" badge rule will need createdAt), while the four the list
 * actually renders are named explicitly.
 */
export const toEventTypes = (payload) => {
  const list = Array.isArray(payload) ? payload : payload?.eventTypes || [];
  return list.map((t) => ({
    id: t.id,
    displayName: t.displayName,
    technicalValue: t.technicalValue,
    iconKey: t.iconKey || null,
    isDefault: Boolean(t.isDefault),
    // The immutable system-default type. Derived server-side as
    // `technical_value == "default"` (EventFieldConfigDao) and sent on every
    // response, so the FE never has to match on the magic string itself.
    // DISTINCT FROM isDefault above — see isSystemType in constants.js.
    isSystem: Boolean(t.isSystem),
    // Counts come from the BE rather than being derived here: the list renders
    // before any field list is fetched, so deriving them would show 0 until the
    // user clicked each type in turn.
    spatialFieldCount: t.spatialFieldCount ?? 0,
    onClickFieldCount: t.onClickFieldCount ?? 0,
    // RENAMED BY BE on 1a5240b043: the column was `source_id`, holding
    // `ctx.productIds.head` (an application id) while the timeline looked it up
    // by the alert's AlertSource — so nothing configured through the UI ever
    // resolved. It is now `username`, written and read as the logged-in user.
    // Kept on the mapped object for parity with the response; no component
    // reads it.
    username: t.username,
    eventCategory: t.eventCategory,
    createdAt: t.createdAt,
    /* The "New" badge. Derived here rather than in the component so the whole
       list agrees on one clock — mapping per render would let rows cross the
       threshold at different moments. See PAGE_OPENED_AT: the rule is an
       assumption, not a specification. */
    isNew: isNewType(t.createdAt)
  }));
};

/**
 * The write shape. `SaveFieldsRequest` takes the WHOLE list and replaces, and
 * only five of the seven keys are read — isIdentifier and isDeprecated are
 * derived server-side and ignored here, so they are dropped rather than sent
 * back as if they were ours to set.
 */
export const fromFields = (fields) =>
  fields.map((f, i) => ({
    fieldKey: f.fieldKey,
    displayLabel: f.displayLabel ?? null,
    showSpatial: Boolean(f.showSpatial),
    showOnClick: Boolean(f.showOnClick),
    // Position in the list IS the order — see FieldConfigTable, which renders in
    // array order. Re-derived on save so a future reorder control needs no
    // separate bookkeeping.
    sortOrder: i,
    // SENT EVEN THOUGH THE BE IGNORES THEM. Its model comment reads
    // "isIdentifier and isDeprecated are derived on read; ignored on write", and
    // they ARE ignored — but `EventTypeFieldEntry` declares them as plain
    // Boolean, not Option, so circe cannot DECODE a body that omits them. Saving
    // without these returned 400 "Unable to parse request body" (measured
    // 2026-08-31); adding them returns 200.
    //
    // Ignored-on-write means their VALUE does not matter, so the local flags are
    // sent as-is rather than invented. If the BE later makes them optional this
    // can go, but it costs nothing to keep.
    isIdentifier: Boolean(f.isIdentifier),
    isDeprecated: Boolean(f.isDeprecated)
  }));

/**
 * Applies a rename to one field, resolving what an EMPTY label means.
 *
 * `display_label` is nullable on the BE and null means "use the schema's own
 * name", so clearing the box is how an admin UNDOES a rename without having to
 * remember the original. That makes the fallback chain the whole point of this
 * function: the typed label, else the schema's current wording, else the key
 * itself so a row is never nameless.
 *
 * Resolving against the schema at rename time rather than storing a copy is
 * deliberate — an admin who clears a rename gets the CURRENT schema wording, not
 * a stale snapshot of it.
 *
 * PURE, and here rather than in the screen, because the screen's copy could not
 * be reached by a test: the rename entry point is parked (see
 * FieldConfigTable's RENAME_ENABLED), so nothing in the UI calls it.
 */
export const renamedFields = (fields, fieldKey, label, schema = []) =>
  fields.map((f) =>
    f.fieldKey === fieldKey
      ? {
          ...f,
          displayLabel:
            label ||
            schema.find((s) => s.technicalName === fieldKey)?.displayName ||
            fieldKey
        }
      : f
  );
